// src/pages/register-options/RegisterTableContadoFormateo.jsx
import React, {
    useState,
    useRef,
    forwardRef,
    useImperativeHandle,
    useEffect,
} from "react";
import axios from "axios";
import {
    handleTextInput,
    handleNumberInput,
    handleCustomInput,
    handlePriceInput,
} from "../../utils/inputUtils";
import PreloadFetch from "../PreloadFetch";

import moment from "moment-timezone";
import "moment/locale/es";
moment.locale("es");

/* ============ CONFIG API DNI ============ */
const DNI_API_URL = "https://apiperu.dev/api/dni";
const DNI_API_BEARER = process.env.REACT_APP_APIPERU_TOKEN;

/* ============ API interna (mismo patrón que Cuota) ============ */
const API_BASE = process.env.REACT_APP_API || "";
const CREDIT_BY_DNI_URL =
    process.env.REACT_APP_REGISTER_CREDIT_BY_DNI ||
    `${API_BASE}/register/credit-by-dni`;

/* ============ AXIOS ============ */
const axiosDni = axios.create({
    baseURL: DNI_API_URL,
    headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${DNI_API_BEARER || ""}`,
    },
    withCredentials: false,
    validateStatus: () => true,
});

const axiosInternal = axios.create({
    baseURL: API_BASE,
    withCredentials: true,
});

// helper para elegir crédito vigente (igual que en Cuota)
import { pickActiveCredit } from "../../utils/HerlperRegisters";

const RegisterTableContadoFormateo = forwardRef((props, ref) => {
    const { onSendDataCat, onSendDataSubCat, onSendDataSegment } = props;

    const [autoOn, setAutoOn] = useState(true);
    const [loading, setLoading] = useState(false);
    const [lastKey, setLastKey] = useState("");

    const createDateSet = moment
        .tz("America/Lima")
        .format("dddd DD [de] MMMM [del] YYYY");

    const catRef = useRef();
    const subCatRef = useRef();
    const segmentRef = useRef();
    const descriptionRef = useRef();
    const nameRef = useRef();
    const paternalSurRef = useRef();
    const maternalSurRef = useRef();
    const imeiRef = useRef();
    const dniRef = useRef();
    const emailRef = useRef();
    const phoneNumberRef = useRef();
    const cashAmountRef = useRef();
    const onlinePayRef = useRef();
    const onlineAmountRef = useRef();
    const applicanRef = useRef();

    // abort/debounce
    const abortDniRef = useRef(null);
    const abortCreditRef = useRef(null);
    const debounceRef = useRef(null);

    /* ==================== Imperative API ==================== */
    useImperativeHandle(ref, () => ({
        getValues: () => ({
            cat: catRef.current?.value,
            subCat: subCatRef.current?.value,
            segment: segmentRef.current?.value, // 1: Payjoy, 2: MovilesPay
            description: descriptionRef.current?.value,
            name: nameRef.current?.value,
            paternalSur: paternalSurRef.current?.value,
            maternalSur: maternalSurRef.current?.value,
            imei: imeiRef.current?.value,
            dni: dniRef.current?.value,
            email: emailRef.current?.value,
            phoneNumber: phoneNumberRef.current?.value,
            cashAmount: cashAmountRef.current?.value,
            onlinePay: onlinePayRef.current?.value,
            onlineAmount: onlineAmountRef.current?.value,
            applicant: applicanRef.current?.value,
        }),
    }));

    /* ==================== Limpieza / helpers ==================== */
    const clearPending = () => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
            debounceRef.current = null;
        }
        try { abortDniRef.current?.abort?.(); } catch {}
        try { abortCreditRef.current?.abort?.(); } catch {}
    };

    const clearNames = ({ keepDni = true } = {}) => {
        if (!keepDni && dniRef.current) dniRef.current.value = "";
        if (nameRef.current) nameRef.current.value = "";
        if (paternalSurRef.current) paternalSurRef.current.value = "";
        if (maternalSurRef.current) maternalSurRef.current.value = "";
    };

    const toTitleCase = (s = "") =>
        s
            .toLowerCase()
            .replace(/\s+/g, " ")
            .trim()
            .replace(/\b([a-záéíóúñü])([a-záéíóúñü]*)/gi, (_, a, b) => a.toUpperCase() + b);

    const setNamesFromApi = (payload) => {
        const root = payload?.data ? payload.data : payload || {};
        const nombres = toTitleCase(root.nombres || root.nombre || "");
        const apPat = toTitleCase(root.apellido_paterno || root.apellidoPaterno || "");
        const apMat = toTitleCase(root.apellido_materno || root.apellidoMaterno || "");
        if (nameRef.current) nameRef.current.value = nombres;
        if (paternalSurRef.current) paternalSurRef.current.value = apPat;
        if (maternalSurRef.current) maternalSurRef.current.value = apMat;
    };

    /* ==================== Fetchers ==================== */
    const fetchDniApi = async (dniVal) => {
        if (!/^\d{8}$/.test(dniVal)) return null;
        if (!DNI_API_BEARER) return null;
        try {
            try { abortDniRef.current?.abort?.(); } catch {}
            abortDniRef.current = new AbortController();
            const res = await axiosDni.post(
                "",
                { dni: dniVal },
                { signal: abortDniRef.current.signal }
            );
            if (res.status === 200 && res.data) return res.data;
            return null;
        } catch (err) {
            console.error("[DNI API] Error:", err?.message || err);
            return null;
        }
    };

    // === EXACTO AL DE CUOTA: POST a /register/credit-by-dni (o var env)
    const fetchCreditByDni = async (dniVal) => {
        try {
            try { abortCreditRef.current?.abort?.(); } catch {}
            abortCreditRef.current = new AbortController();

            const auth = localStorage.getItem("token") || "";
            const res = await axiosInternal.post(
                CREDIT_BY_DNI_URL.replace(API_BASE, ""),
                { dni: dniVal },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: auth ? `Bearer ${auth}` : "",
                    },
                    signal: abortCreditRef.current.signal,
                }
            );
            // puede venir como { data }, { credit } o plano:
            return res?.data?.data || res?.data?.credit || res?.data || null;
        } catch (err) {
            console.error("[CREDIT BY DNI] Error:", err);
            return null;
        }
    };

    /* ==================== Autocomplete core ==================== */
    // Autocompletar Nombres/Apellidos (ambos segmentos)
    const autoFillNamesByDni = (dniVal, useDebounce = true) => {
        if (!autoOn) return;
        if (useDebounce) {
            if (debounceRef.current) clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => doAutoNames(dniVal), 1500);
        } else {
            doAutoNames(dniVal);
        }
    };

    const doAutoNames = async (dniVal) => {
        if (!autoOn) return;
        const key = `${dniVal}-seg${segmentRef.current?.value || ""}`;
        if (lastKey === key) return;
        setLastKey(key);

        setLoading(true);
        try {
            const dniInfo = await fetchDniApi(dniVal);
            if (dniInfo) setNamesFromApi(dniInfo);
        } finally {
            setLoading(false);
        }
    };

    // Solo para MovilesPay: trae crédito vigente y completa IMEI/email/phone
    const fetchAndFillCreditExtra = async (dniVal) => {
        if (String(segmentRef.current?.value) !== "2") return; // solo MovilesPay
        setLoading(true);
        try {
            const payload = await fetchCreditByDni(dniVal);
            const row = pickActiveCredit(payload);

            if (row) {
                // llena IMEI/EMAIL/PHONE según mapeo solicitado
                if (imeiRef.current && row.fr_imei) imeiRef.current.value = row.fr_imei;
                if (emailRef.current && row.fr_email) emailRef.current.value = row.fr_email;
                if (phoneNumberRef.current && row.fr_phone_number)
                    phoneNumberRef.current.value = row.fr_phone_number;
            } else {
                // si no hay crédito vigente, opcionalmente puedes limpiar (no se pidió alert aquí)
                // emailRef.current && (emailRef.current.value = "");
                // phoneNumberRef.current && (phoneNumberRef.current.value = "");
                // imeiRef.current && (imeiRef.current.value = "");
            }
        } finally {
            setLoading(false);
        }
    };

    /* ==================== Handlers ==================== */
    const onDniChange = (e) => {
        handleNumberInput(e, 8);
        const v = (e.target.value || "").trim();
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
            debounceRef.current = null;
        }
        if (v.length === 8) {
            if (autoOn) autoFillNamesByDni(v, true);
            if (String(segmentRef.current?.value) === "2") {
                // mismo debounce para no duplicar llamadas
                debounceRef.current = setTimeout(() => fetchAndFillCreditExtra(v), 1500);
            }
        }
    };

    const changeCat = (e) => {
        onSendDataCat?.({ cat: e.target.value });
        clearPending();
        clearNames({ keepDni: true });
    };

    const changeSubCat = (e) => {
        onSendDataSubCat?.({
            cat: catRef.current.value,
            subCat: e.target.value,
            segment: segmentRef.current.value,
        });
        clearPending();
        clearNames({ keepDni: true });
        const v = (dniRef.current?.value || "").trim();
        if (autoOn && /^\d{8}$/.test(v)) {
            autoFillNamesByDni(v, false);
            if (String(segmentRef.current?.value) === "2") fetchAndFillCreditExtra(v);
        }
    };

    const changeSegment = (e) => {
        onSendDataSegment?.({
            cat: catRef.current.value,
            subCat: subCatRef.current.value,
            segment: e.target.value,
        });
        clearPending();
        clearNames({ keepDni: true });
        const v = (dniRef.current?.value || "").trim();
        if (autoOn && /^\d{8}$/.test(v)) {
            autoFillNamesByDni(v, false);
            if (String(e.target.value) === "2") fetchAndFillCreditExtra(v);
        }
    };

    useEffect(() => {
        return () => {
            try { abortDniRef.current?.abort?.(); } catch {}
            try { abortCreditRef.current?.abort?.(); } catch {}
        };
    }, []);

    /* ==================== Render ==================== */
    return (
        <div className="registerTable registerTable7 registerTableInList">
            {loading && <PreloadFetch className="mainPreloadOpacity" />}

            <table>
                <caption>
                    <div>
                        <span>{createDateSet}</span>
                        <label className="toggleTwo" style={{ marginLeft: 12 }}>
                            <input
                                type="checkbox"
                                className="toggleTwo-input"
                                checked={autoOn}
                                onChange={(e) => {
                                    setAutoOn(e.target.checked);
                                    if (e.target.checked) {
                                        const v = (dniRef.current?.value || "").trim();
                                        if (/^\d{8}$/.test(v)) {
                                            autoFillNamesByDni(v, false);
                                            if (String(segmentRef.current?.value) === "2") {
                                                fetchAndFillCreditExtra(v);
                                            }
                                        }
                                    } else {
                                        clearPending();
                                    }
                                }}
                            />
                            <span className="toggleTwo-slider" />
                            <span className="toggleTwo-text">Autocompletar por DNI</span>
                        </label>
                    </div>
                </caption>

                <thead>
                    <tr>
                        <th>Tipo de Registro</th>
                        <th>Tipo de Producto</th>
                        <th>Fuente</th>
                        <th>Descripción</th>
                        <th>Nombre</th>
                        <th>Apellido 1</th>
                        <th>Apellido 2</th>
                        <th>IMEI</th>
                        <th>DNI</th>
                        <th>EMAIL</th>
                        <th>Celular</th>
                        <th>Monto Efectivo</th>
                        <th>Pago Online</th>
                        <th>Monto Online</th>
                    </tr>
                </thead>

                <tbody>
                    <tr>
                        <td className="cat">
                            <select defaultValue="2" onChange={changeCat} ref={catRef}>
                                <option value="1">Crédito</option>
                                <option value="2">Contado</option>
                                <option value="3">Egreso</option>
                                <option value="4">Ingreso</option>
                                <option value="5">Retiro</option>
                            </select>
                        </td>

                        <td className="subCat">
                            <select defaultValue={"4"} onChange={changeSubCat} ref={subCatRef}>
                                <option value="1">Télefono</option>
                                <option value="2">Cuota</option>
                                <option value="3">Accesorios</option>
                                <option value="4">Formateo</option>
                                <option value="5">Chip</option>
                                <option value="6">FM</option>
                            </select>
                        </td>

                        <td className="segment">
                            <select defaultValue="1" onChange={changeSegment} ref={segmentRef}>
                                <option value="1">Payjoy</option>
                                <option value="2">MovilesPay</option>
                            </select>
                        </td>

                        <td width="12%" className="tableWidth">
                            <input
                                type="text"
                                ref={descriptionRef}
                                onInput={(e) => handleCustomInput(e, 100)}
                                id="descriptionAdd"
                            />
                        </td>

                        {/* Nombre / Apellidos: deshabilitados cuando el toggle está ON */}
                        <td>
                            <input
                                type="text"
                                onInput={(e) => handleTextInput(e, 40)}
                                ref={nameRef}
                                id="nameAdd"
                                disabled={autoOn}
                                aria-disabled={autoOn}
                            />
                        </td>
                        <td>
                            <input
                                type="text"
                                onInput={(e) => handleTextInput(e, 40)}
                                ref={paternalSurRef}
                                id="paternalSurAdd"
                                disabled={autoOn}
                                aria-disabled={autoOn}
                            />
                        </td>
                        <td>
                            <input
                                type="text"
                                onInput={(e) => handleTextInput(e, 40)}
                                ref={maternalSurRef}
                                id="maternalSurAdd"
                                disabled={autoOn}
                                aria-disabled={autoOn}
                            />
                        </td>

                        <td width="6.5%">
                            <input
                                type="number"
                                onInput={(e) => handleNumberInput(e, 15)}
                                ref={imeiRef}
                                id="imeiAdd"
                            />
                        </td>

                        <td>
                            <input
                                type="number"
                                onInput={(e) => handleNumberInput(e, 8)}
                                onChange={onDniChange}
                                ref={dniRef}
                                id="dniAdd"
                            />
                        </td>

                        <td width="10%">
                            <input type="text" ref={emailRef} id="emailAdd" />
                        </td>

                        <td>
                            <input
                                type="number"
                                onInput={(e) => handleNumberInput(e, 9)}
                                ref={phoneNumberRef}
                                id="phoneNumberAdd"
                            />
                        </td>

                        <td width="5%" className="withSymbol">
                            <span>S/.</span>
                            <input
                                type="text"
                                defaultValue={0.0}
                                onInput={handlePriceInput}
                                ref={cashAmountRef}
                            />
                        </td>

                        <td>
                            <select defaultValue="0" ref={onlinePayRef} id="onlinePayAdd">
                                <option value="0">Selecciónar</option>
                                <option value="1">Yape</option>
                                <option value="5">Culqi Yape</option>
                                <option value="2">Tarjeta</option>
                                <option value="6">Culqi Tarjeta</option>
                                <option value="3">Transferencia</option>
                            </select>
                        </td>

                        <td width="5%" className="withSymbol">
                            <span>S/.</span>
                            <input
                                type="text"
                                defaultValue={0.0}
                                onInput={handlePriceInput}
                                ref={onlineAmountRef}
                                id="onlineAmountAdd"
                            />
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
});

export default RegisterTableContadoFormateo;
